/**
 * SocketContext — replaces AngularJS SocketService + $rootScope.$broadcast.
 *
 * Original pattern:
 *   socket.on('driver', data => $rootScope.$broadcast('MessageReceived', data))
 *   socket.on('extra',  data => $rootScope.$broadcast('ExtraReceived', data))
 *   socket.on('reload', data => $rootScope.$broadcast('ReloadRunsheet', data))
 *
 * React replacement:
 *   - Single socket.io-client instance held in context.
 *   - Components subscribe via `useSocketEvent(eventName, handler)`.
 *   - `send(event, data)` emits to server.
 *   - Connect is called once when the user is authenticated.
 *
 * The client stays on socket.io-client 2.x because the chat server runs
 * socket.io 1.7 for the legacy Android app. A 3.x+ client fails the handshake
 * against it. Move to 4.x once the server is upgraded (or allows EIO3).
 */

import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import io from 'socket.io-client';
import { CHAT_SERVER } from '@/env';
import type { Driver } from '@/types';

interface SocketContextValue {
  send: (event: string, data: unknown) => void;
  socket: SocketIOClient.Socket | null;
}

const SocketContext = createContext<SocketContextValue>({
  send: () => undefined,
  socket: null,
});

interface SocketProviderProps {
  children: React.ReactNode;
  driver: Driver | null;
  isAuthenticated: boolean;
}

export function SocketProvider({ children, driver, isAuthenticated }: SocketProviderProps) {
  // State rather than a ref: useSocketEvent subscribers need a re-render once
  // the socket exists, or they never attach their handlers.
  const [socket, setSocket] = useState<SocketIOClient.Socket | null>(null);

  useEffect(() => {
    if (!isAuthenticated || !driver) return;

    const newSocket = io(CHAT_SERVER, { transports: ['websocket'] });
    setSocket(newSocket);

    newSocket.on('connect', () => {
      newSocket.emit('driversignin', driver);
    });

    return () => {
      newSocket.disconnect();
      setSocket(null);
    };
  }, [isAuthenticated, driver]);

  const send = useCallback(
    (event: string, data: unknown) => {
      socket?.emit(event, data);
    },
    [socket],
  );

  const value = useMemo(() => ({ send, socket }), [send, socket]);

  return <SocketContext.Provider value={value}>{children}</SocketContext.Provider>;
}

export function useSocket(): SocketContextValue {
  return useContext(SocketContext);
}

/**
 * Subscribe to a socket.io event for the lifetime of the calling component.
 * Replaces: $rootScope.$on('EventName', handler) inside controllers.
 *
 * Usage:
 *   useSocketEvent<Message>('MessageReceived', (msg) => { ... });
 */
export function useSocketEvent<T = unknown>(event: string, handler: (data: T) => void): void {
  const { socket } = useSocket();

  useEffect(() => {
    if (!socket) return;
    socket.on(event, handler as (...args: unknown[]) => void);
    return () => {
      socket.off(event, handler as (...args: unknown[]) => void);
    };
  }, [socket, event, handler]);
}
